import React, { FC, Fragment } from 'react';
import { formatDate, getShortLanguageCode } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { Box, Button, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { TransportLine, TransportOrder, TransportServices } from 'models/transport';
import { NShiftShipmentWaybill } from 'models/nshift-shipment-waybill';
import { downloadUrl } from 'utils/file';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { CONSTANTS } from 'config/constants';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { transportDateHelper } from 'utils/transport-date-helper';

const { NSHIFT_DEVELOPER_ID } = CONSTANTS;

type TransportLinesForOrderProps = {
    transportLinesForOrder: TransportLine[];
};
const TransportLinesForOrder: FC<TransportLinesForOrderProps> = ({
    transportLinesForOrder,
}: TransportLinesForOrderProps) => {
    const { t: translate } = useTranslation(['orders']);
    const currentUser = currentUserVar();

    const displayDateFor = (transportOrder: TransportOrder) => {
        if (!transportOrder.transportDate) {
            return new Date();
        }
        const pickupFromLabDate = new Date(transportOrder.transportDate);
        if (transportOrder.serviceCode !== TransportServices.DELIVERY) {
            return pickupFromLabDate;
        }
        return transportOrder.isSameDayDelivery
            ? pickupFromLabDate
            : transportDateHelper.nextWorkingDay(pickupFromLabDate, true);
    };

    const showTransportOrder = (transportOrder: TransportOrder) => {
        const url = `/transport/transport-schedule?date=${displayDateFor(transportOrder).valueOf()}&transportLine=true`;
        localStorage.setItem('transportLine', JSON.stringify(transportOrder));
        window.open(url, '_blank');
    };

    const openTrackingLink = (event: React.MouseEvent<HTMLElement>, waybill: NShiftShipmentWaybill) => {
        event.stopPropagation();
        event.preventDefault();
        window.open(
            `https://www.unifaunonline.com/ext.uo.${getShortLanguageCode(currentUser.localeFormat?.language)}.track?key=${NSHIFT_DEVELOPER_ID}&order=${waybill.orderNo}`,
        );
    };

    const downloadWaybill = (event: React.MouseEvent<HTMLElement>, waybill: NShiftShipmentWaybill) => {
        event.stopPropagation();
        event.preventDefault();
        downloadUrl(`${waybill.prints[0].id}.pdf`, waybill.prints[0].fileUrl);
    };

    return (
        <Fragment>
            <List dense>
                {transportLinesForOrder.map((transportLine: TransportLine) => {
                    return (
                        <Fragment key={transportLine._id}>
                            {transportLine.transportOrders?.map((transportOrder: TransportOrder) => {
                                return (
                                    <ListItem
                                        key={transportLine._id}
                                        onClick={() => showTransportOrder(transportOrder)}
                                        disablePadding
                                    >
                                        <ListItemButton data-test="transport-lines-for-orders.transport-order-btn">
                                            <ListItemText
                                                sx={{ color: 'primary.main' }}
                                                primary={`${translate(`transport:${transportOrder.serviceCode}`)} ${formatDate(displayDateFor(transportOrder), 'date')}`}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                            {transportLine.waybills?.map((waybill: NShiftShipmentWaybill) => {
                                return (
                                    <Box key={waybill._id} sx={{ mt: 1 }}>
                                        <Box>
                                            <Typography color="success.main" variant="subtitle2">
                                                {`${translate(`transport:WAYBILL_PRINTED`)}: ${formatDate(waybill.shipDate || new Date(), 'date')}`}
                                            </Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center">
                                            <Button
                                                sx={{ marginRight: 1 }}
                                                onClick={(e) => openTrackingLink(e, waybill)}
                                                size="small"
                                                data-test="transport-lines-for-orders.track-shipment-btn"
                                            >
                                                {translate('transport:TRACK_SHIPMENT')}
                                            </Button>
                                            <Button
                                                onClick={(e) => downloadWaybill(e, waybill)}
                                                size="small"
                                                data-test="transport-lines-for-orders.print-waybill-btn"
                                            >
                                                {translate('transport:PRINT_WAYBILL')}
                                            </Button>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Fragment>
                    );
                })}
            </List>
        </Fragment>
    );
};

export default TransportLinesForOrder;
